import React, { useState, useEffect } from "react";
import { ListaDePatrones } from "../listaDePatrones/ListaDePatrones";
import "./PatronesView.css";

const PatronesView = ({ fetchSorteos }) => {
  const [patronesDisponibles, setPatronesDisponibles] = useState([]);
  const [patronesOrdenados, setPatronesOrdenados] = useState([]);
  const [patronSeleccionado, setPatronSeleccionado] = useState(null);
  const [nombreSorteo, setNombreSorteo] = useState("");
  const [fechaSorteo, setFechaSorteo] = useState("");
  const [sorteosDisponibles, setSorteosDisponibles] = useState([]);
  const [sorteoSeleccionado, setSorteoSeleccionado] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [mensajeConfirmacion, setMensajeConfirmacion] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;

  // 🔄 Cargar lista de patrones y sorteos disponibles
  useEffect(() => {
    const fetchPatrones = () => {
      fetch(`${API_URL}/patrons_list`)
        .then((res) => res.json())
        .then((data) => setPatronesDisponibles(data))
        .catch((err) => console.error("Error al obtener patrones:", err));
    };

    const fetchSorteosDisponibles = () => {
      fetch(`${API_URL}/sorteos_disponibles/`)
        .then((res) => res.json())
        .then((data) => setSorteosDisponibles(data))
        .catch((err) =>
          console.error("Error al obtener sorteos disponibles:", err),
        );
    };

    fetchPatrones();
    fetchSorteosDisponibles();

    // Si hay función de refresh del padre, usarla también
    if (fetchSorteos) {
      fetchSorteos();
    }
  }, [API_URL, fetchSorteos]);

  // 🔄 Al montar la vista, cargar sorteo actual y patrones asociados
  useEffect(() => {
    const cargarInfoSorteoYPatrones = async () => {
      try {
        const res = await fetch(`${API_URL}/info_sorteo_actual`);
        const data = await res.json();
        actualizarEstadoSorteoLocal(data);
      } catch (err) {
        console.error("Error al cargar sorteo y patrones:", err);
      }
    };

    cargarInfoSorteoYPatrones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  const actualizarEstadoSorteoLocal = async (info) => {
    setNombreSorteo(info.nombreSorteo);
    setFechaSorteo(info.fechaSorteo);
    setSorteoSeleccionado(info);

    const resPatrones = await fetch(
      `${API_URL}/patrones_sorteo/${info.idSorteo || info._id}`,
    );
    const patronesData = await resPatrones.json();

    if (Array.isArray(patronesData.patrones_secuencia)) {
      const patronesFormateados = patronesData.patrones_secuencia.map(
        (p, index) => ({
          _id: p._id,
          nombre: p.nombre,
          monto: p.monto || 0,
          activo: p.activo || false,
          orden: index,
        }),
      );

      // Si el sorteo está en pausa, desactivamos visualmente los patrones
      if (info.actual === true && info.activo === false) {
        setPatronesOrdenados(
          patronesFormateados.map((p) => ({ ...p, activo: false })),
        );
        setPatronSeleccionado(null);
      } else {
        setPatronesOrdenados(patronesFormateados);
        const activo = patronesFormateados.find((p) => p.activo);
        if (activo) setPatronSeleccionado(activo);
      }
    }
  };

  const seleccionarSorteo = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      await fetch(`${API_URL}/seleccionar_sorteo/${id}`, { 
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (fetchSorteos) fetchSorteos();

      // 🔹 Obtener info del sorteo actual
      const resInfo = await fetch(`${API_URL}/info_sorteo_actual`);
      const info = await resInfo.json();

      info._id = info._id?.toString();
      info.idSorteo = info.idSorteo?.toString();

      setSorteoSeleccionado(info);
      setNombreSorteo(info.nombreSorteo);
      setFechaSorteo(info.fechaSorteo);

      // 🔹 Obtener patrones asociados
      const resPatrones = await fetch(
        `${API_URL}/patrones_sorteo/${info.idSorteo}`,
      );
      const patronesData = await resPatrones.json();

      let patronesFormateados = Array.isArray(patronesData.patrones_secuencia)
        ? patronesData.patrones_secuencia.map((p, index) => ({
            _id: p._id,
            nombre: p.nombre,
            monto: p.monto || 0,
            activo: p.activo || false,
            orden: index,
          }))
        : [];

      // 🔹 Si el sorteo es el ACTUAL pero NO está ACTIVO, forzamos la desactivación visual
      if (info.actual === true && info.activo === false) {
        // Forzar todos los patrones a inactivos en frontend
        patronesFormateados = patronesFormateados.map((p) => ({
          ...p,
          activo: false,
        }));

        setPatronesOrdenados(patronesFormateados);
        setPatronSeleccionado(null);

        // 🚫 salir aquí para que no se ejecute la lógica normal
        return;
      }

      // 🔹 Caso normal
      setPatronesOrdenados(patronesFormateados);

      const activo = patronesFormateados.find((p) => p.activo);
      if (activo) setPatronSeleccionado(activo);
    } catch (error) {
      console.error("Error al seleccionar sorteo:", error);
    }
  };

  const asociarSecuencia = () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setMensajeConfirmacion("❌ No hay sesión activa");
      return;
    }

    setConfirmando(true);
    setMensajeConfirmacion("");
    fetch(`${API_URL}/asociar_patrones_sorteo`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        patrones: patronesOrdenados.map((p) => ({
          patron: p._id,
          monto: p.monto || 0,
        })),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setMensajeConfirmacion("✅ Éxito al asociar la secuencia.");
        // Sincronizar con el otro módulo
        if (fetchSorteos) {
          fetchSorteos();
        }

        // Inicializar participaciones para el bingo actual
        // Obtener el primer patrón de la secuencia como el inicial
        if (patronesOrdenados.length > 0 && sorteosDisponibles.length > 0) {
          const token = localStorage.getItem("access_token");
          const sorteoActual = sorteosDisponibles.find((s) => s.actual);
          if (sorteoActual && patronesOrdenados[0]._id) {
            fetch(
              `${API_URL}/participaciones/iniciar_bingo?sorteo_id=${sorteoActual._id}&patron_id=${patronesOrdenados[0]._id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              },
            )
              .then((res) => res.json())
              .then((data) => {
                console.log("Participaciones inicializadas:", data);
              })
              .catch((err) => {
                console.error("Error al inicializar participaciones:", err);
              });
          }
        }
      })
      .catch((err) => {
        setMensajeConfirmacion("❌ Error al asociar la secuencia.");
      })
      .finally(() => setConfirmando(false));
  };

  return (
    <div className="patrones-container">
      {/* Barra Superior */}
      <div className="patrones-status-bar">
        <div className="sorteo-info">
          <div className="sorteo-nombre">{nombreSorteo}</div>
          <div className="sorteo-fecha">Fecha: {fechaSorteo}</div>
        </div>
        <button
          className="refresh-btn"
          onClick={() => {
            fetch(`${API_URL}/info_sorteo_actual`)
              .then((res) => res.json())
              .then(actualizarEstadoSorteoLocal);
          }}
        >
          🔄
        </button>
      </div>

      {/* Tarjeta Principal */}
      <div className="patrones-main-card">
        <div className="patrones-header">
          <h3>Gestión de Patrones</h3>
          <span className="count-badge">{patronesOrdenados.length}</span>
        </div>

        <div className="patrones-scroll-area">
          {/* Selector de Sorteo */}
          <div className="sorteo-selector">
            <label>Seleccionar Sorteo:</label>
            <select
              onChange={(e) => seleccionarSorteo(e.target.value)}
              value={sorteoSeleccionado?._id?.toString() || ""}
            >
              <option value="">-- Selecciona un sorteo --</option>
              {sorteosDisponibles.map((sorteo) => (
                <option key={String(sorteo._id)} value={String(sorteo._id)}>
                  {sorteo.nombreSorteo} ({sorteo.fechaSorteo})
                </option>
              ))}
            </select>
          </div>

          {/* Lista de Patrones */}
          <ListaDePatrones
            disponibles={patronesDisponibles}
            ordenados={patronesOrdenados}
            setOrdenados={setPatronesOrdenados}
            patronSeleccionado={patronSeleccionado}
            setSeleccionado={setPatronSeleccionado}
            sorteoSeleccionado={sorteoSeleccionado}
            setPatronesOrdenados={setPatronesOrdenados}
          />

          {/* Botón Confirmar */}
          <button
            className="btn-confirmar"
            onClick={asociarSecuencia}
            disabled={patronesOrdenados.length === 0 || confirmando}
          >
            {confirmando
              ? "Confirmando..."
              : "✅ Confirmar secuencia de patrones"}
          </button>

          {mensajeConfirmacion && (
            <div
              className={`mensaje-confirmacion ${
                mensajeConfirmacion.includes("✅") ? "exito" : "error"
              }`}
            >
              {mensajeConfirmacion}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatronesView;
